import { useMemo } from 'react';
import { theme, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/hooks/use-stores';
import { EPSelect } from '@/components';
import PlusIcon from '@/comp/common/icons/PlusIcon';
import { EngineStore } from '@/store/Engine/EngineStore';
import { IgnoreFallbackCompressorsContainer } from './styled';
import IgnoreFallbackCompressorsTooltip from './IgnoreFallbackCompressorsTooltip';
import { InfoCircleOutlined } from '@ant-design/icons';

const IgnoreFallbackCompressors = observer(() => {
  const { t } = useTranslation();
  const { engineStore }: { engineStore: EngineStore } = useStores();
  const { token } = theme.useToken();

  const compressorOptions = useMemo(() => {
    if (!engineStore?.applicableCompressorsForEngine?.length) return [];
    return engineStore.applicableCompressorsForEngine.map(compressor => ({
      label: compressor.name,
      value: compressor.id,
    }));
  }, [engineStore.applicableCompressorsForEngine]);

  const ignoreFallbackCompressors = engineStore.fallbackScenarios?.ignoreFallbackCompressors || [];

  const preselectedCompressors = useMemo(() => {
    if (!ignoreFallbackCompressors.length) return [];
    return ignoreFallbackCompressors.filter(compressorId =>
      compressorOptions.some(option => option.value === compressorId),
    );
  }, [ignoreFallbackCompressors, compressorOptions]);

  const handleCompressorSelectionChange = (selectedCompressorIds: string[]) => {
    engineStore.fallbackScenarios?.setIgnoreFallbackCompressors(selectedCompressorIds);
  };

  return (
    <IgnoreFallbackCompressorsContainer $isReadOnly={engineStore.isReadOnly}>
      <div className="container-compressor-dropdown-fallback-two-point-o">
        <div className="label-container">
          {t('ignoreFallbackCompressors')}
          <Popover placement="bottom" content={<IgnoreFallbackCompressorsTooltip />} trigger="hover">
            <InfoCircleOutlined className="info-icon" />
          </Popover>
        </div>
        <EPSelect
          mode="multiple"
          placeholder={t('selectCompressors') || 'Select compressors'}
          className="compressor-dropdown-fallback-two-point-o"
          value={preselectedCompressors}
          options={compressorOptions}
          onChange={handleCompressorSelectionChange}
          disabled={engineStore.isReadOnly}
          suffixIcon={<PlusIcon color={token.colorBorder} />}
          filterOption={(input, option) => option?.label && option.label?.toLowerCase()?.includes(input?.toLowerCase())}
          style={{ minWidth: 400 }}
        />
      </div>
    </IgnoreFallbackCompressorsContainer>
  );
});

export default IgnoreFallbackCompressors;
